/**
 * Forgot Password Modal
 *
 * Modal for password reset functionality.
 * Currently displays the reset link (no email sending).
 */

import React, { useState } from 'react';
import { X, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import apiClient from '@shared/services/apiClient';

interface ForgotPasswordModalProps {
  visible: boolean;
  onClose: () => void;
}

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ visible, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsLoading(true);

    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      setResetLink(response.data.reset_link || '');
      setSuccess(true);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setResetLink('');
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-dark-surface border border-dark-border/30 rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-border/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-600/20 text-brand-400 rounded-xl flex items-center justify-center border border-brand-500/20">
              <Mail size={20} />
            </div>
            <h2 className="text-lg md:text-xl font-bold text-white">Forgot Password</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-dark-muted hover:text-white transition-all duration-150"
            data-testid='unknown-close-btn'
            data-ux='close'
            data-interaction-id='unknown-close-btn'>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-900/20 border border-red-900/50 text-red-300 px-4 py-3 rounded text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-900/20 border border-green-900/50 text-green-300 px-4 py-3 rounded text-sm flex items-center gap-2">
              <CheckCircle size={16} />
              Password reset link generated successfully!
            </div>
          )}

          {!success && (
            <>
              <div className="space-y-2">
                <label className="text-xs font-bold text-dark-muted uppercase tracking-wider ml-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-dark-bg border border-dark-border/30 text-white/90 text-sm rounded px-4 py-2.5 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 outline-none transition-all"
                  placeholder="Enter your email address"
                  required
                  data-testid='unknown-enter-your-email-address-input'
                  data-ux='enter-your-email-address-edit'
                  data-interaction-id='unknown-enter-your-email-address-input' />
              </div>

              <p className="text-xs text-dark-muted">
                We'll send you a password reset link to your email address.
              </p>
            </>
          )}

          {success && resetLink && (
            <div className="space-y-2">
              <label className="text-xs font-bold text-dark-muted uppercase tracking-wider ml-1">
                Reset Link (Development Only)
              </label>
              <div className="bg-dark-bg border border-dark-border/30 text-dark-text text-xs rounded px-4 py-2.5 break-all">
                {resetLink}
              </div>
              <p className="text-xxs text-dark-muted">
                In production, this link would be sent via email.
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            {!success && (
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-brand-600 hover:bg-brand-500 text-white font-bold py-2.5 rounded-md transition-all disabled:opacity-50"
                data-testid='unknown-action-btn'
                data-ux='action-submit'
                data-interaction-id='unknown-action-btn'>
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 bg-dark-bg border border-dark-border/30 text-dark-text hover:bg-dark-surface/50 font-medium py-2.5 rounded-md transition-all"
              data-testid='unknown-close-btn'
              data-ux='close'
              data-interaction-id='unknown-close-btn'>
              {success ? 'Done' : 'Cancel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
